import { getOfflineModeActive } from '@/utils/offline-mode';
import {
  gql,
  MutationResult,
  MutationTuple,
  useMutation,
} from '@apollo/client';
import {
  NewOrder_OrderContextQuery,
  NewOrder_OrderFieldsFragment,
  NewOrder_UpdateFulfillmentMutation,
  NewOrder_UpdateFulfillmentMutationVariables,
  Orders_Set_Input,
  OrderStatus,
} from '@/types';
import { useCallback, useState } from 'react';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineOrderContext } from './use-offline-data';

export const UPDATE_FULFILLMENT_MUTATION = gql`
  mutation NewOrder_UpdateFulfillment(
    $orderUUID: uuid!
    $set: orders_set_input!
    $unplacedOrderStatuses: [String!]!
  ) {
    update_orders(
      where: {
        uuid: { _eq: $orderUUID }
        status: { _in: $unplacedOrderStatuses }
      }
      _set: $set
    ) {
      returning {
        fulfillment_type
        restaurant_desired_delivery_time
        fulfillment_timeslot_start
        fulfillment_timeslot_end
        uuid
      }
    }
  }
`;

interface UpdateFulFillmentParams {
  variables: {
    orderUUID: string;
    set: Orders_Set_Input;
    unplacedOrderStatuses: OrderStatus[];
  };
}

type UpdateFulfillmentMutationTuple = [
  MutationTuple<
    NewOrder_UpdateFulfillmentMutation,
    NewOrder_UpdateFulfillmentMutationVariables
  >[0],
  Pick<MutationResult<NewOrder_UpdateFulfillmentMutation>, 'loading'>,
];

const useOfflineUpdateFulfillment = (
  restaurantUUID: string,
  supplierUUID: string,
): [
  (
    variables: UpdateFulFillmentParams,
  ) => Promise<{ data: NewOrder_UpdateFulfillmentMutation }>,
  UpdateFulfillmentMutationTuple[1],
] => {
  const { draftOrder, refetchOfflineDraftOrder } = useNewOrderContext();
  const { offlineOrderContext, saveOfflineOrderContext } =
    useOfflineOrderContext(restaurantUUID, supplierUUID);

  const [loading, setLoading] = useState(false);
  const updateOfflineFulfillment = useCallback(
    async ({ variables }: UpdateFulFillmentParams) => {
      const { orderUUID, set } = variables;
      setLoading(true);

      let data: NewOrder_UpdateFulfillmentMutation = {};

      if (!draftOrder) {
        return { data };
      }

      // update local draft order state
      const newDraftOrder: NewOrder_OrderFieldsFragment = {
        ...draftOrder,
        fulfillment_type: set.fulfillment_type!,
        restaurant_desired_delivery_time: set.restaurant_desired_delivery_time,
      };
      await refetchOfflineDraftOrder();

      // update local offline order context state
      const newContextToSave: NewOrder_OrderContextQuery = {
        ...offlineOrderContext.data,
        getAvailableOrderFulfillments:
          offlineOrderContext?.data?.getAvailableOrderFulfillments || [],
        getOrders: [{ order: newDraftOrder }],
      };
      saveOfflineOrderContext(newContextToSave);

      // return data that would be otherwise returned by the online mutation
      data = {
        update_orders: {
          returning: [
            {
              ...set,
              fulfillment_type: set.fulfillment_type!,
              restaurant_desired_delivery_time:
                set.restaurant_desired_delivery_time,
              uuid: orderUUID,
            },
          ],
        },
      };
      setLoading(false);
      return { data };
    },
    [
      draftOrder,
      offlineOrderContext.data,
      refetchOfflineDraftOrder,
      saveOfflineOrderContext,
    ],
  );

  return [updateOfflineFulfillment, { loading }];
};

const useOnlineUpdateFulfillment = () => {
  return useMutation<
    NewOrder_UpdateFulfillmentMutation,
    NewOrder_UpdateFulfillmentMutationVariables
  >(UPDATE_FULFILLMENT_MUTATION);
};

export const useUpdateFulfillment = ({
  restaurantUUID,
  supplierUUID,
}: {
  restaurantUUID: string;
  supplierUUID: string;
}) => {
  const [updateOfflineFulfillment, { loading: offlineLoading }] =
    useOfflineUpdateFulfillment(restaurantUUID, supplierUUID);
  const [updateOnlineFulfillment, { loading: onlineLoading }] =
    useOnlineUpdateFulfillment();

  const updateFulfillment = useCallback(
    async (params: UpdateFulFillmentParams) => {
      const isOfflineModeActive = await getOfflineModeActive();
      if (isOfflineModeActive) {
        return await updateOfflineFulfillment(params);
      } else {
        return await updateOnlineFulfillment(params);
      }
    },
    [updateOfflineFulfillment, updateOnlineFulfillment],
  );

  return {
    isUpdatingFulfillment: offlineLoading || onlineLoading,
    updateFulfillment,
  };
};
